import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { liveFeedStore } from "../../lib/liveFeedStore";

const API_BASE = process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";

// Per-session profile cache shared across all LiveFeed mounts. Batched
// lookup hits POST /api/profiles/batch with all unknown wallets in one
// request; null entries (no profile) are cached too so we don't refetch.
const profileCache = new Map();
// Single in-flight batch keyed by a shared promise. All callers during
// the 50ms collection window subscribe to the same promise and resolve
// together AFTER the fetch completes (the previous version polled on
// `pendingBatch` going null, which fired before the fetch finished —
// callers read an empty cache and never showed usernames).
let pendingBatch = null;
const queueBatch = (wallets) => {
  if (!pendingBatch) {
    let resolveFn;
    const promise = new Promise((r) => { resolveFn = r; });
    const batch = { wallets: new Set(), promise, resolve: resolveFn };
    pendingBatch = batch;
    setTimeout(async () => {
      pendingBatch = null;
      const list = Array.from(batch.wallets);
      try {
        const r = await fetch(`${API_BASE}/api/profiles/batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ wallets: list }),
        });
        if (!r.ok) throw new Error(`batch ${r.status}`);
        const { profiles } = await r.json();
        for (const w of list) profileCache.set(w, profiles?.[w] ?? null);
      } catch {
        for (const w of list) profileCache.set(w, null);
      }
      batch.resolve();
    }, 50);
  }
  for (const w of wallets) pendingBatch.wallets.add(w);
  return pendingBatch.promise;
};

const truncWallet = (w) => (w && w.length >= 4 ? w.slice(-4) : w || "");

const formatUsd = (n) => {
  if (!Number.isFinite(n)) return "$0";
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  if (n >= 100) return `$${n.toFixed(0)}`;
  if (n >= 10) return `$${n.toFixed(1)}`;
  return `$${n.toFixed(2)}`;
};

// Compact whole-number format for burn chip (token amounts, not dollars).
const formatCompactNumber = (n) => {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(0);
};

// Inline SVGs so the chip doesn't depend on a separate icon font / fetch.
// Both render at the same 18px footprint so they slot into the avatar
// position without a layout shift.
const FlameIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2c1 3 4 4 4 8a4 4 0 0 1-1 2.7C16 11 17 9 17 9c2 1 4 4 4 8a9 9 0 1 1-18 0c0-4 3-7 5-9 0 0 0 3 1 5 .6 1 2 1 2-1 0-2-1-4 1-10z"
    />
  </svg>
);
const SproutIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      d="M12 22V11M12 11c0-3 2-5 6-5-1 4-3 6-6 6zm0-1c0-3-2-5-6-5 1 4 3 6 6 6zM6 22h12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

// Seed `profiles` state from the module-level cache so a remount (e.g.
// navigating back to the home page) shows usernames immediately for any
// wallet we already looked up this session — otherwise state starts empty,
// the effect below sees the cache is hit and skips the fetch, and render
// falls back to truncated wallets until a brand new trade arrives.
function seedProfilesFromCache(entries) {
  const seed = {};
  for (const e of entries) {
    if (!e.wallet) continue;
    const cached = profileCache.get(e.wallet);
    if (cached) seed[e.wallet] = cached;
  }
  return seed;
}

export default function LiveFeed() {
  const [entries, setEntries] = useState(() => liveFeedStore.getEntries());
  const [profiles, setProfiles] = useState(() => seedProfilesFromCache(liveFeedStore.getEntries()));
  // IDs that just appeared this session — used for the slide-in animation.
  const [newIds, setNewIds] = useState(() => new Set());
  // IDs we've ever seen — anything not in here that shows up is "new".
  // Seed entries (already in the store on first mount) are NOT animated;
  // only entries that arrive after mount get the shake.
  const seenIdsRef = useRef(new Set(liveFeedStore.getEntries().map((e) => e.id)));

  useEffect(() => liveFeedStore.subscribe((next) => setEntries(next)), []);

  // Listen for profile snippets that the backend pre-attaches to trade:new
  // and token:new WS events. Seed both the module-level cache (so future
  // mounts get the value for free) AND the local `profiles` state (so the
  // current render frame can use it). This is what makes new chips show
  // the username on the same frame they appear, with no flash of wallet.
  useEffect(() => {
    return liveFeedStore.subscribeProfiles((profile) => {
      if (!profile?.wallet) return;
      const cleaned = profile.username || profile.image_url
        ? { username: profile.username, image_url: profile.image_url }
        : null;
      profileCache.set(profile.wallet, cleaned);
      if (cleaned) {
        setProfiles((p) => p[profile.wallet] ? p : { ...p, [profile.wallet]: cleaned });
      }
    });
  }, []);

  // Detect freshly-added entries since last render and flag them for ~700ms
  // so the CSS pop-in shake plays exactly once per new chip.
  useEffect(() => {
    const fresh = [];
    for (const e of entries) {
      if (!seenIdsRef.current.has(e.id)) {
        seenIdsRef.current.add(e.id);
        fresh.push(e.id);
      }
    }
    if (fresh.length === 0) return;
    setNewIds((prev) => {
      const next = new Set(prev);
      for (const id of fresh) next.add(id);
      return next;
    });
    const t = setTimeout(() => {
      setNewIds((prev) => {
        const next = new Set(prev);
        for (const id of fresh) next.delete(id);
        return next;
      });
    }, 750);
    return () => clearTimeout(t);
  }, [entries]);

  // For each entry: if its wallet is already cached (this session) but not
  // yet in component state, hoist it in immediately. Otherwise queue it in
  // the next batch fetch. Splitting these two paths is what stops a
  // remount from briefly flashing wallet addresses for wallets we've
  // already resolved.
  useEffect(() => {
    const unknown = [];
    const fromCache = {};
    const seen = new Set();
    for (const e of entries) {
      if (!e.wallet || seen.has(e.wallet)) continue;
      seen.add(e.wallet);
      if (profiles[e.wallet]) continue;
      if (profileCache.has(e.wallet)) {
        const cached = profileCache.get(e.wallet);
        if (cached) fromCache[e.wallet] = cached;
        continue;
      }
      unknown.push(e.wallet);
    }
    if (Object.keys(fromCache).length > 0) {
      setProfiles((p) => ({ ...p, ...fromCache }));
    }
    if (unknown.length === 0) return;
    let cancelled = false;
    queueBatch(unknown).then(() => {
      if (cancelled) return;
      const next = {};
      for (const w of unknown) {
        const row = profileCache.get(w);
        if (row) next[w] = row;
      }
      if (Object.keys(next).length > 0) setProfiles((p) => ({ ...p, ...next }));
    });
    return () => { cancelled = true; };
  }, [entries, profiles]);

  // Horizontal row of "event tiles" — bigger than chips, card-like, with
  // an internal two-line layout (verb + timestamp on top, actor + amount
  // + token on bottom). Color comes from a left-edge accent stripe per
  // action type. Newest at the left, oldest drops off the right when
  // count exceeds MAX_VISIBLE. No marquee, no scrolling, no duplication.
  const MAX_VISIBLE = 5;
  const visible = useMemo(() => entries.slice(0, MAX_VISIBLE), [entries]);

  if (entries.length === 0) {
    return (
      <section className="pm-livefeed pm-livefeed--empty" aria-label="Live activity">
        <div className="pm-livefeed__head">
          <span className="pm-livefeed__pulse" aria-hidden="true" />
          <span className="pm-livefeed__label">Live activity</span>
        </div>
        <div className="pm-livefeed__empty-text">Waiting for the next move…</div>
      </section>
    );
  }

  return (
    <section className="pm-livefeed" aria-label="Live activity">
      <div className="pm-livefeed__head">
        <span className="pm-livefeed__pulse" aria-hidden="true" />
        <span className="pm-livefeed__label">Live activity</span>
      </div>
      <div className="pm-livefeed__row">
        {visible.map((e) => {
          if (e.kind === "burn" || e.kind === "compound") {
            const isBurn = e.kind === "burn";
            const verb = isBurn ? "burned" : "added to LP";
            const amount = isBurn
              ? formatCompactNumber(e.amount_whole)
              : formatUsd(e.usd);
            return (
              <Link
                key={e.id}
                to={`/token/${e.mint}`}
                className={`pm-livefeed__tile pm-livefeed__tile--${e.kind}${newIds.has(e.id) ? " is-new" : ""}`}
              >
                <div className="pm-livefeed__tile-top">
                  <span className="pm-livefeed__tile-icon" aria-hidden="true">
                    {isBurn ? <FlameIcon /> : <SproutIcon />}
                  </span>
                  <span className="pm-livefeed__tile-verb">{verb}</span>
                </div>
                <div className="pm-livefeed__tile-bot">
                  <span className="pm-livefeed__tile-amount">{amount}</span>
                  {e.image_url ? (
                    <img src={e.image_url} alt="" className="pm-livefeed__tile-img" />
                  ) : null}
                  <span className="pm-livefeed__tile-ticker">${e.symbol}</span>
                </div>
              </Link>
            );
          }
          const profile = profiles[e.wallet];
          const displayName = profile?.username
            ? `@${profile.username}`
            : truncWallet(e.wallet);
          const verb = e.kind === "buy" ? "bought" : e.kind === "sell" ? "sold" : "launched";
          return (
            <Link
              key={e.id}
              to={`/token/${e.mint}`}
              className={`pm-livefeed__tile pm-livefeed__tile--${e.kind}${newIds.has(e.id) ? " is-new" : ""}`}
            >
              <div className="pm-livefeed__tile-top">
                <span className="pm-livefeed__tile-avatar" aria-hidden="true">
                  {profile?.image_url ? (
                    <img src={profile.image_url} alt="" />
                  ) : (
                    <span className="pm-livefeed__tile-avatar-fb">
                      {(profile?.username || e.wallet || "?").slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="pm-livefeed__tile-name">{displayName}</span>
                <span className="pm-livefeed__tile-verb">{verb}</span>
              </div>
              <div className="pm-livefeed__tile-bot">
                {e.kind !== "launch" && (
                  <span className="pm-livefeed__tile-amount">{formatUsd(e.usd)}</span>
                )}
                {e.image_url ? (
                  <img src={e.image_url} alt="" className="pm-livefeed__tile-img" />
                ) : null}
                <span className="pm-livefeed__tile-ticker">${e.symbol}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
